import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, KeyRound, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";

const HERO_DOORS = "https://images.unsplash.com/photo-1518882542732-7b2826f7b1bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwY2xhc3NpYyUyMGFyY2hpdGVjdHVyZSUyMGRvdWJsZSUyMGRvb3JzfGVufDB8fHx8MTc4NDE2NzQ3NHww&ixlib=rb-4.1.0&q=85";
const PATHWAY = "https://images.unsplash.com/photo-1691943703496-977ff624d182?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHx3YXJtJTIwbGlnaHQlMjBwYXRod2F5JTIwc3VidGxlfGVufDB8fHx8MTc4NDE2NzQ3NHww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ebooks?featured=true").then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/subscribe", { email, source: "hero" });
      try { localStorage.setItem("aperture_captured", "1"); } catch {}
      toast.success("Free sample incoming.");
      setEmail("");
    }
    catch { toast.error("Something went wrong."); }
    setBusy(false);
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Opening doors to new possibilities"
        description="Practical playbooks for creative real estate and housing solutions — tax debt escapes, foreclosure workouts, subject-to acquisitions, and investor strategies from Aperture Enterprises."
        canonical="https://apertureent.com/"
        image="https://images.unsplash.com/photo-1518882542732-7b2826f7b1bd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-6 relative z-10">
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">Aperture Enterprises · Since 2023</div>
            <h1 className="aperture-h1 text-5xl md:text-7xl leading-[1.02]">
              Opening doors <br />
              <span className="italic" style={{ color: "#C4A265" }}>to new possibilities.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-700 leading-relaxed max-w-lg font-sans">
              Practical, board-tested playbooks for creative real estate — from tax debt escapes to subject-to acquisitions. Written for the ones who move quietly and act decisively.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/catalog" data-testid="hero-browse-btn" className="btn-primary px-8 py-4 text-xs uppercase tracking-[0.24em] font-medium inline-flex items-center gap-3 rounded-sm">
                Browse the Catalog <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <form onSubmit={submit} className="flex gap-2 flex-1 sm:max-w-sm" data-testid="hero-lead-form">
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Get a free sample" data-testid="hero-lead-email" className="flex-1 px-4 py-4 bg-white border border-navy-10 text-navy placeholder-navy/40 text-sm focus:outline-none focus:border-gold rounded-sm" />
                <button disabled={busy} data-testid="hero-lead-submit" className="px-5 py-4 border border-navy text-navy hover:bg-navy hover:text-white text-xs uppercase tracking-widest transition-colors duration-300 rounded-sm">Send</button>
              </form>
            </div>
            <div className="mt-14 flex items-center gap-8 text-xs text-navy/60 uppercase tracking-widest">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" strokeWidth={1.5} />Secure Delivery</div>
              <div className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-gold" strokeWidth={1.5} />Instant Access</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-gold" strokeWidth={1.5} />Curated</div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] door-arch overflow-hidden shadow-2xl">
              <img src={HERO_DOORS} alt="Aperture" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/40 via-transparent to-transparent" />
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <div className="eyebrow text-white/80 mb-2" style={{ color: "#C4A265" }}>The Aperture Doctrine</div>
                <div className="font-serif text-2xl leading-tight">Every distressed door hides an opportunity — for the seller, and for the strategist.</div>
              </div>
            </div>
            <div className="absolute -bottom-10 -left-10 hidden lg:block w-40 h-40 bg-gold/20 rounded-full blur-3xl" />
          </div>
        </div>
        <div className="gold-hairline max-w-7xl mx-auto" />
      </section>

      {/* VALUE PROPOSITION */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-3 gap-12">
        {[
          { t: "For Homeowners in Distress", d: "Escape hatches for tax debt, foreclosure, and inherited property tangles — before the county clock runs out." },
          { t: "For Investors & Wholesalers", d: "Subject-to, tax-lien plays, seller financing, and off-market deal flow, distilled from operators who close weekly." },
          { t: "For Realtors & Advisors", d: "Client-ready frameworks and scripts that transform your service — and your referral value." },
        ].map((v, i) => (
          <div key={i} data-testid={`value-card-${i}`} className="border-t border-navy pt-6">
            <div className="eyebrow mb-3">0{i + 1}</div>
            <h3 className="aperture-h2 text-2xl mb-3">{v.t}</h3>
            <p className="text-slate-600 leading-relaxed">{v.d}</p>
          </div>
        ))}
      </section>

      {/* FEATURED CATALOG */}
      <section className="bg-white border-y border-navy-10" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="eyebrow mb-3">The Library</div>
              <h2 className="aperture-h2 text-4xl md:text-5xl">Featured playbooks.</h2>
            </div>
            <Link to="/catalog" data-testid="featured-view-all" className="hidden md:inline-flex items-center gap-2 text-sm text-navy hover:text-gold transition-colors">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-14">
            {featured.map((b) => (
              <Link key={b.id} to={`/ebook/${b.slug}`} data-testid={`featured-ebook-${b.slug}`} className="book-card group">
                <div className="aspect-[3/4] bg-[#F7F5F0] overflow-hidden shadow-xl rounded-sm flex items-center justify-center p-4">
                  <img src={b.cover_image_url} alt={b.title} className="max-w-full max-h-full w-auto h-auto object-contain" />
                </div>
                <div className="mt-6">
                  <div className="eyebrow mb-2">{b.category}</div>
                  <div className="font-serif text-xl text-navy leading-tight">{b.title}</div>
                  <div className="mt-3 text-gold text-sm">${b.price.toFixed(2)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PATHWAY / ABOUT */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="eyebrow mb-4">Our Doctrine</div>
          <h2 className="aperture-h2 text-4xl md:text-5xl leading-tight">A quiet approach<br />to loud problems.</h2>
          <p className="mt-8 text-slate-700 leading-relaxed">
            Aperture publishes for a specific reader — the operator, the homeowner, the strategist who prefers due diligence over hype. Our books read like private memos: direct, referenced, and immediately usable.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-8">
            <div><div className="font-serif text-3xl text-navy">24 hrs</div><div className="text-xs text-navy/60 uppercase tracking-widest mt-1">Instant Download</div></div>
            <div><div className="font-serif text-3xl text-navy">100%</div><div className="text-xs text-navy/60 uppercase tracking-widest mt-1">Satisfaction Guaranteed</div></div>
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2 relative">
          <div className="aspect-[5/4] overflow-hidden shadow-2xl">
            <img src={PATHWAY} alt="Pathway" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
